import { useState } from "react";

const ORIGINS = [
  { id: "california", x: 148, y: 148, label: "California / USA" },
  { id: "afghanistan", x: 670, y: 158, label: "Afghanistan" },
  { id: "pakistan", x: 690, y: 174, label: "Pakistan" },
  { id: "india", x: 716, y: 204, label: "India" },
  { id: "nepal", x: 733, y: 178, label: "Nepal" },
  { id: "bangladesh", x: 751, y: 192, label: "Bangladesh" },
  { id: "vietnam", x: 793, y: 218, label: "Vietnam" },
];

const HUB = { x: 650, y: 186, label: "Dubai, UAE" };

const DESTINATIONS = [
  { id: "west-africa", x: 490, y: 240, label: "Africa" },
  { id: "east-africa", x: 604, y: 284, label: "Africa" },
  { id: "south-africa", x: 566, y: 350, label: "Africa" },
  { id: "global", x: 506, y: 116, label: "Global Markets" },
];

const CONTINENTS = [
  "M60,120 Q95,58 172,70 Q258,58 300,108 Q322,158 272,190 Q232,232 182,212 Q122,232 92,182 Q50,162 60,120 Z",
  "M252,252 Q300,230 330,272 Q352,330 312,390 Q282,422 260,380 Q232,330 252,252 Z",
  "M468,90 Q520,68 562,94 Q578,130 546,156 Q500,172 474,140 Q454,114 468,90 Z",
  "M468,192 Q540,170 592,202 Q622,252 600,322 Q570,372 528,362 Q478,342 464,282 Q448,222 468,192 Z",
  "M582,88 Q700,48 832,80 Q902,110 880,170 Q850,232 768,242 Q700,262 640,222 Q580,192 572,140 Q566,108 582,88 Z",
  "M790,322 Q850,300 892,332 Q902,372 860,387 Q810,392 784,362 Q774,336 790,322 Z",
];

const arc = (a, b) => {
  const mx = (a.x + b.x) / 2;
  const lift = Math.min(150, Math.max(46, Math.hypot(b.x - a.x, b.y - a.y) * 0.26));
  return `M ${a.x} ${a.y} Q ${mx} ${Math.min(a.y, b.y) - lift} ${b.x} ${b.y}`;
};

const Marker = ({ p, color, testId, onHover }) => (
  <g
    data-testid={testId}
    onMouseEnter={() => onHover(p)}
    onMouseLeave={() => onHover(null)}
    className="cursor-pointer"
  >
    <circle cx={p.x} cy={p.y} r="14" fill={color} opacity="0.12">
      <animate attributeName="r" values="8;20;8" dur="3.2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.25;0;0.25" dur="3.2s" repeatCount="indefinite" />
    </circle>
    <circle cx={p.x} cy={p.y} r="4.5" fill={color} stroke="#09121F" strokeWidth="1.5" />
  </g>
);

export const WorldMap = ({ className = "" }) => {
  const [hover, setHover] = useState(null);

  return (
    <div className={`relative ${className}`} data-testid="world-map">
      <svg viewBox="0 0 1000 460" className="h-auto w-full" role="img" aria-label="World trade map">
        <defs>
          <linearGradient id="routeIn" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#DFB756" />
          </linearGradient>
          <linearGradient id="routeOut" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#8FC1A9" />
            <stop offset="100%" stopColor="#8FC1A9" stopOpacity="0.15" />
          </linearGradient>
        </defs>

        {[0, 1, 2, 3, 4].map((i) => (
          <line key={`h${i}`} x1="0" y1={60 + i * 85} x2="1000" y2={60 + i * 85} stroke="#22364A" strokeWidth="0.5" opacity="0.5" />
        ))}
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <line key={`v${i}`} x1={80 + i * 140} y1="0" x2={80 + i * 140} y2="460" stroke="#22364A" strokeWidth="0.5" opacity="0.5" />
        ))}

        {CONTINENTS.map((d, i) => (
          <path key={i} d={d} fill="rgba(212,175,55,0.07)" stroke="rgba(212,175,55,0.28)" strokeWidth="1" />
        ))}

        {ORIGINS.map((o, i) => {
          const d = arc(o, HUB);
          return (
            <g key={`in-${o.id}`}>
              <path d={d} fill="none" stroke="url(#routeIn)" strokeWidth="1.4" className="route-line" opacity="0.85" />
              <circle r="3" fill="#E6C46A">
                <animateMotion dur={`${4 + i * 0.6}s`} repeatCount="indefinite" path={d} />
              </circle>
            </g>
          );
        })}

        {DESTINATIONS.map((t, i) => {
          const d = arc(HUB, t);
          return (
            <g key={`out-${t.id}`}>
              <path d={d} fill="none" stroke="url(#routeOut)" strokeWidth="1.4" className="route-line" opacity="0.85" />
              <circle r="3" fill="#8FC1A9">
                <animateMotion dur={`${4.5 + i * 0.7}s`} repeatCount="indefinite" path={d} />
              </circle>
            </g>
          );
        })}

        {ORIGINS.map((o) => (
          <Marker key={o.id} p={o} color="#D4AF37" testId="global-map-origin-marker" onHover={setHover} />
        ))}
        {DESTINATIONS.map((t) => (
          <Marker key={t.id} p={t} color="#8FC1A9" testId="global-map-destination-marker" onHover={setHover} />
        ))}

        <g data-testid="global-map-hub-marker" onMouseEnter={() => setHover(HUB)} onMouseLeave={() => setHover(null)} className="cursor-pointer">
          <circle cx={HUB.x} cy={HUB.y} r="22" fill="#D4AF37" opacity="0.15">
            <animate attributeName="r" values="14;30;14" dur="2.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0;0.3" dur="2.6s" repeatCount="indefinite" />
          </circle>
          <circle cx={HUB.x} cy={HUB.y} r="7" fill="#DFB756" stroke="#09121F" strokeWidth="2" />
          <text x={HUB.x} y={HUB.y - 18} textAnchor="middle" fill="#E6C46A" fontSize="11" fontFamily="Outfit, sans-serif" letterSpacing="2">
            DUBAI · UAE
          </text>
        </g>

        {hover && (
          <g pointerEvents="none">
            <rect
              x={Math.min(Math.max(hover.x - 70, 8), 860)}
              y={hover.y - 46}
              width="140"
              height="26"
              rx="4"
              fill="#0B1D16"
              stroke="rgba(212,175,55,0.5)"
              strokeWidth="1"
            />
            <text
              x={Math.min(Math.max(hover.x, 78), 930)}
              y={hover.y - 28}
              textAnchor="middle"
              fill="#F4EFE6"
              fontSize="12"
              fontFamily="Outfit, sans-serif"
            >
              {hover.label}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};
